import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { PenLine, FileText, BadgeCheck, MessageCircle, Sparkles } from "lucide-react";

type Props = { pt: boolean; ctaHref: string; ctaLabel: string; onVideo: () => void };

/**
 * Full-screen parallax opening (reference: "parallax jungle theme"), rebuilt for digital signature:
 * layered paper landscape instead of trees, floating pens/documents instead of birds, big script word,
 * velocity-based motion blur. Everything moves with transforms inside one rAF, never top/left.
 * Text always sits above the layers (the reference hid its title behind the forest).
 */
export function ParallaxHero({ pt, ctaHref, ctaLabel, onVideo }: Props) {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current; if (!el) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const items = Array.from(el.querySelectorAll<HTMLElement>("[data-px]"));
    const blurables = Array.from(el.querySelectorAll<HTMLElement>("[data-blur]"));
    let last = window.scrollY, lastT = performance.now(), blur = 0, raf = 0;
    const frame = () => {
      raf = 0;
      const y = Math.min(window.scrollY, window.innerHeight * 1.2);
      const now = performance.now();
      const v = Math.abs(window.scrollY - last) / Math.max(1, now - lastT); // px per ms
      last = window.scrollY; lastT = now;
      blur = Math.min(10, Math.max(blur * 0.82, v * 6));
      for (const it of items) {
        const sx = Number(it.dataset.px || 0), sy = Number(it.dataset.py || 0), sc = Number(it.dataset.ps || 0);
        it.style.transform = `translate3d(${y * sx}px, ${y * sy}px, 0) scale(${1 + y * sc})`;
      }
      for (const b of blurables) b.style.filter = blur > 0.3 ? `blur(${blur.toFixed(1)}px)` : "";
      if (blur > 0.3) raf = requestAnimationFrame(frame);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(frame); };
    frame();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, []);

  const floaters: Array<[typeof PenLine, string, string, string]> = [
    // icon, position classes, px, py  (px/py = how many px per scrolled px; birds fly out and up)
    [PenLine, "left-[8%] top-[22%] h-10 w-10 rotate-[-25deg] text-[var(--fs-forest)]", "-1.6", "-1.1"],
    [FileText, "left-[18%] top-[62%] h-9 w-9 rotate-[8deg] text-[var(--fs-moss)]", "-2.4", "-0.6"],
    [BadgeCheck, "right-[10%] top-[26%] h-10 w-10 text-[var(--fs-moss)]", "1.8", "-1.3"],
    [MessageCircle, "right-[20%] top-[58%] h-8 w-8 text-[#1B7F3B]", "2.6", "-0.5"],
    [Sparkles, "left-[42%] top-[14%] h-7 w-7 text-[var(--fs-sage)]", "-0.6", "-2.0"],
    [FileText, "right-[36%] top-[70%] h-8 w-8 rotate-[-10deg] text-[var(--fs-forest)]", "1.2", "-0.9"],
  ];

  return (
    <div ref={root} className="fs-px relative h-[100svh] min-h-[640px] overflow-hidden bg-[linear-gradient(180deg,#FBFDF8_0%,#EEF6E4_55%,#DCEBC8_100%)]">
      {/* floaters (birds) */}
      {floaters.map(([Icon, cls, px, py], i) => (
        <span key={i} data-px={px} data-py={py} data-blur className={`pointer-events-none absolute hidden opacity-80 will-change-transform md:block ${cls}`} aria-hidden="true">
          <Icon className="h-full w-full" strokeWidth={1.4} />
        </span>
      ))}

      {/* copy — above every layer */}
      <div data-px="0" data-py="-0.5" data-blur className="absolute inset-x-0 top-[38%] z-20 -translate-y-1/2 px-5 text-center will-change-transform">
        <p className="mx-auto max-w-2xl text-[11px] font-medium tracking-[0.16em] text-[var(--fs-moss)] sm:text-base sm:tracking-[0.18em]">
          {pt ? "A NOVA FORMA DE FECHAR CONTRATOS" : "THE NEW WAY TO CLOSE DEALS"}
        </p>
        <h1 className="fs-script mt-2 text-[26vw] leading-[0.95] text-[var(--fs-forest)] sm:text-[14vw] lg:text-[11vw]">
          {pt ? "Assine." : "Sign."}
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-gray-700 sm:text-lg">
          {pt ? "Com IA, pelo WhatsApp e dentro do Dynamics 365. Validade jurídica, sem imprimir nada." : "With AI, over WhatsApp and inside Dynamics 365. Legally valid, nothing to print."}
        </p>
      </div>

      {/* CTA drops down on scroll (like the reference button) */}
      <div data-px="0" data-py="1.2" className="absolute inset-x-0 top-[64%] z-20 flex flex-col items-center justify-center gap-3 px-5 will-change-transform sm:top-[66%] sm:flex-row">
        <a href={ctaHref} className="w-full sm:w-auto"><Button size="lg" className="h-12 w-full rounded-full sm:w-auto bg-[var(--fs-forest)] px-7 text-base text-white shadow-lg hover:bg-[var(--fs-moss)]">{ctaLabel}</Button></a>
        <Button size="lg" variant="outline" className="h-12 w-full rounded-full sm:w-auto border-[var(--fs-forest)]/30 bg-white/70 px-7 text-base text-[var(--fs-forest)] backdrop-blur hover:bg-white" onClick={onVideo}>{pt ? "Ver o vídeo" : "Watch the video"}</Button>
      </div>

      {/* paper landscape: back sheet moves down slowly (forest), front sheet rises (rocks) */}
      <svg data-px="0" data-py="0.25" className="absolute -bottom-2 left-0 z-[5] w-[120%] will-change-transform" viewBox="0 0 1440 420" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 260 C 240 180, 420 330, 720 240 S 1200 120, 1440 220 L1440 420 L0 420 Z" fill="#CDE3B4" />
      </svg>
      <svg data-px="0" data-py="0.1" className="absolute -bottom-2 -left-[6%] z-[6] w-[112%] will-change-transform" viewBox="0 0 1440 360" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 230 C 300 120, 520 300, 820 210 S 1250 110, 1440 200 L1440 360 L0 360 Z" fill="#A5D66F" />
      </svg>
      <svg data-px="0" data-py="-0.12" className="absolute -bottom-2 left-0 z-[7] w-full will-change-transform" viewBox="0 0 1440 300" preserveAspectRatio="none" aria-hidden="true">
        <path d="M0 200 C 360 120, 600 260, 900 180 S 1300 100, 1440 170 L1440 300 L0 300 Z" fill="#4A6D2E" />
      </svg>
      {/* signature line drawn across the front sheet */}
      <svg className="pointer-events-none absolute bottom-[6%] left-[10%] z-[8] w-[80%]" viewBox="0 0 1200 80" aria-hidden="true">
        <path className="fs-sign-stroke" d="M10 50 C 120 10, 200 70, 300 40 S 480 10, 560 45 S 720 75, 820 35 S 980 8, 1080 40 S 1160 60, 1190 42" fill="none" stroke="#FBFDF8" strokeWidth="2.5" strokeLinecap="round" opacity="0.9" />
      </svg>
      {/* blend into the next section (light, not blue) */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[9] h-32 bg-[linear-gradient(to_top,#2D5016,rgba(45,80,22,0))]" aria-hidden="true" />
    </div>
  );
}
